// src/pages/tools/components/ToolCodeTab.jsx
import React, { useState } from 'react';
import {
  Grid,
  Paper,
  Box,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Alert,
  Card,
  CardContent,
  Divider,
  useTheme
} from '@mui/material';
import {
  FileCopy as CopyIcon,
  Refresh as ResetIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import Editor from '@monaco-editor/react';
import { useNavigate } from 'react-router-dom'; // For help icon

// Helper to extract docstring (can be moved to a utils file)
const extractDocstring = (code) => {
  if (!code) return "No docstring found.";
  const docstringMatch = code.match(/"""([\s\S]*?)"""/); // Basic match
  return docstringMatch ? docstringMatch[1].trim() : "No docstring found or not in standard format.";
};

const ToolCodeTab = ({
  toolCode,
  initialToolCode,
  onCodeChange,
  onResetCode,
  toolName,
  toolDescription,
  toolDefaultParams,
  // theme, // Passed implicitly by useTheme or can be explicit prop
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [codePreviewVisible, setCodePreviewVisible] = useState(true);
  const [codeCopied, setCodeCopied] = useState(false);

  const handleCopyCodeToClipboard = () => {
    navigator.clipboard.writeText(toolCode);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const toggleCodePreview = () => {
    setCodePreviewVisible(!codePreviewVisible);
  };
  
  const hasUnsavedCodeChanges = toolCode !== initialToolCode;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={codePreviewVisible ? 8 : 12}>
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Python Code</Typography>
            <Box>
              <Button variant="outlined" size="small" onClick={toggleCodePreview} sx={{ mr: 1 }}>
                {codePreviewVisible ? "Hide Preview" : "Show Preview"}
              </Button>
              <Tooltip title="Copy Code">
                <IconButton onClick={handleCopyCodeToClipboard} size="small" sx={{ mr: 0.5 }}>
                  {codeCopied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset Code to last saved or template default">
                <span> {/* Span for Tooltip when IconButton is disabled */}
                  <IconButton onClick={onResetCode} size="small" disabled={!hasUnsavedCodeChanges && toolCode === initialToolCode}>
                    <ResetIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="View Tool Writing Documentation">
                <IconButton onClick={() => navigate('/tools/documentation')} size="small">
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            Implement your tool as an async Python function. Use <code>RunContext</code> for context-aware operations.
            Ensure your docstring clearly explains args and returns for the AI.
          </Alert>
          <Box sx={{ flexGrow: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, minHeight: '500px', overflow: 'hidden' }}>
            <Editor
              height="100%"
              language="python"
              value={toolCode || ''}
              onChange={onCodeChange}
              theme={theme.palette.mode === 'dark' ? 'vs-dark' : 'light'}
              options={{ minimap: { enabled: true }, scrollBeyondLastLine: false, fontSize: 14, automaticLayout: true, wordWrap: 'on' }}
            />
          </Box>
          {hasUnsavedCodeChanges && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
              <InfoIcon fontSize="inherit" sx={{ mr: 0.5 }} /> Code has been modified. Save to persist changes.
            </Typography>
          )}
        </Paper>
      </Grid>
      {codePreviewVisible && (
        <Grid item xs={12} md={4}>
          <Card sx={{ position: 'sticky', top: theme.spacing(2), height: { md: 'calc(100vh - 120px)' }, maxHeight: '700px', overflowY: 'auto' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Tool Preview</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                How the AI might interpret your tool's signature and docs.
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" gutterBottom><strong>Name:</strong> {toolName || "Unnamed Tool"}</Typography>
              <Typography variant="body2" paragraph sx={{ maxHeight: '100px', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                <strong>Description:</strong> {toolDescription || "No description"}
              </Typography>
              
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Function Signature:</Typography>
              <Box sx={{ backgroundColor: theme.palette.action.hover, p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem', mb: 1, overflowX: 'auto' }}>
                {(toolCode || '').split('\n')[0]}
              </Box>
              
              <Typography variant="subtitle2" gutterBottom>Docstring:</Typography>
              <Box sx={{ backgroundColor: theme.palette.action.hover, p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', mb: 1, maxHeight: '200px', overflowY: 'auto' }}>
                {extractDocstring(toolCode)}
              </Box>
              
              {toolDefaultParams && toolDefaultParams !== '{}' && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>Default Parameters:</Typography>
                  <Box sx={{ backgroundColor: theme.palette.action.hover, p: 1, borderRadius: 1, fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap', maxHeight: '150px', overflowY: 'auto' }}>
                    {toolDefaultParams}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      )}
    </Grid>
  );
};

export default ToolCodeTab;